// ═══════════════════════════════════════════════════════════════════════════
// rentabilidadEstimada — Rentabilidad ESTIMADA/INFERIDA de Mercado Libre
// ───────────────────────────────────────────────────────────────────────────
// v1 (14-jul-2026): estructura de costos de la CUENTA + margen plano.
// v2 (14-jul-2026, misma noche): INFERIDA con la fórmula real
// (calcularRentabilidadLinea) + cascada de fuentes cuenta → categoría del
// ecosistema → global del ecosistema (doc conocimientoEcosistema/costos-ml)
// + los números de decisión del sheet de Mariano+Mateo: ACOS máximo
// tolerable, inversión máxima en Ads y descuento máximo vs corte.
// Ver docs/conocimiento-ecosistema-rentabilidad-inferida.md.
//
//   margen inferido = fórmula completa con:
//     comisión/envío ← cascada (cuenta → eco-categoría → eco-banda → eco-global)
//     impuestos      ← config fiscal de la empresa (NUNCA del ecosistema)
//     publicidad     ← 0 (es palanca: se decide aparte con acosMax/inversionMax)
//
// PURO: sin Firestore, testeable con fixtures. Los consumidores cargan el doc
// del ecosistema y la config fiscal y se los pasan.
// ═══════════════════════════════════════════════════════════════════════════

import { calcularRentabilidadLinea, derivarCostosNoInformados, COSTOS_DECLARABLES } from './rentabilidad'
import { bandaPrecioDe } from './bandasPrecioPorSite'

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

// ─────────────────────────────────────────────────────────────────────────────
// v1 — Estructura de costos ML de la CUENTA (de sus órdenes Capa 1)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Estructura de costos ML de la cuenta, a partir de órdenes con la Capa 1
 * calculada. Acepta docs canónicos (`o.rentabilidad.porLinea`) o el shape
 * cascada del loader (`o._rentabilidad.porLinea`).
 *
 * `envioPct` (y con él `costosMLPct`) es null si ninguna línea informa el envío.
 *
 * @returns {{ comisionPct, envioPct, impuestosPct, costosMLPct, lineas, subtotalTotal } | null}
 */
export function estructuraCostosML(ordenes = []) {
  let subtotal = 0
  let comision = 0
  let envio = 0
  // Base del envío: sin las líneas con el envío `no-informada` (entran 0 y
  // ese 0 no es «sin costo»: sumarlas bajaría el %).
  let subtotalConEnvio = 0
  let impuestos = 0
  let lineas = 0

  for (const o of (ordenes || [])) {
    const porLinea = o?.rentabilidad?.porLinea || o?._rentabilidad?.porLinea || null
    if (!porLinea) continue
    for (const key of Object.keys(porLinea)) {
      const l = porLinea[key]
      if (!l || l.estado !== 'calculada') continue
      const b = l.breakdown || l
      const sub = num(b.subtotal ?? l.subtotal)
      if (sub <= 0) continue
      subtotal += sub
      comision += num(b.comisionMLFinal)
      if (!derivarCostosNoInformados(l).includes(COSTOS_DECLARABLES.envio)) {
        envio += num(b.envioSeller)
        subtotalConEnvio += sub
      }
      impuestos += num(b.ivaNeto) + num(b.iibb) + num(b.impCredDebito)
      lineas++
    }
  }

  if (lineas === 0 || subtotal <= 0) return null
  const comisionPct = comision / subtotal
  // Sin ninguna línea que informe el envío, el % no se sabe: null (el
  // resolver cae al ecosistema y la estimación queda sin total, nunca con 0).
  const envioPct = subtotalConEnvio > 0 ? envio / subtotalConEnvio : null
  const impuestosPct = impuestos / subtotal
  return {
    comisionPct,
    envioPct,
    impuestosPct,
    costosMLPct: envioPct == null ? null : comisionPct + envioPct + impuestosPct,
    lineas,
    subtotalTotal: subtotal,
  }
}

/**
 * v1 — Margen estimado PLANO (bruto − costos ML % de la cuenta). Queda para
 * compat y para cuentas donde solo importa el pantallazo; la ficha y los
 * agentes usan inferirRentabilidadML (v2, fórmula completa).
 */
export function margenEstimadoML({ precio, costoUnit, estructura = null } = {}) {
  const p = Number(precio)
  const c = Number(costoUnit)
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(c) || c <= 0) return null

  const margenBrutoPct = (p - c) / p
  if (!estructura || !Number.isFinite(estructura.costosMLPct)) {
    return {
      margenBrutoPct,
      costosMLPct: null,
      margenEstimadoPct: margenBrutoPct,
      fuente: 'bruto',
      desglose: { comisionPct: null, envioPct: null, impuestosPct: null },
    }
  }
  return {
    margenBrutoPct,
    costosMLPct: estructura.costosMLPct,
    margenEstimadoPct: margenBrutoPct - estructura.costosMLPct,
    fuente: 'estimado-ml',
    desglose: {
      comisionPct: estructura.comisionPct,
      envioPct: estructura.envioPct,
      impuestosPct: estructura.impuestosPct,
    },
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// v2 — Cascada de fuentes: cuenta → ecosistema (categoría / banda / global)
// ─────────────────────────────────────────────────────────────────────────────

const medianaDe = (seg) => (Number.isFinite(seg?.comisionPct?.mediana) || Number.isFinite(seg?.envioPct?.mediana)) ? seg : null

/**
 * Resuelve comisión % y envío % con la cascada de fuentes. La cuenta manda
 * si tiene historia; si no, el ecosistema — categoría publicable primero,
 * banda de precio para el envío, global como piso. Cada valor declara fuente.
 *
 * @param {object} args
 * @param {object|null} args.estructuraCuenta — estructuraCostosML(órdenes propias)
 * @param {object|null} args.ecosistema       — doc conocimientoEcosistema/costos-ml
 * @param {string|null} args.categoryId       — categoría ML de la publicación
 * @param {number|null} args.precio           — para elegir la banda de envío
 * @returns {{ comisionPct, envioPct, fuenteComision, fuenteEnvio } | null}
 */
export function resolverCostosML({ estructuraCuenta = null, ecosistema = null, categoryId = null, precio = null } = {}) {
  let comisionPct = null
  let envioPct = null
  let fuenteComision = null
  let fuenteEnvio = null

  // 1. La cuenta propia manda (historia real de ESTA cuenta).
  if (estructuraCuenta && Number.isFinite(estructuraCuenta.comisionPct)) {
    comisionPct = estructuraCuenta.comisionPct
    fuenteComision = { tipo: 'cuenta', lineas: estructuraCuenta.lineas }
  }
  if (estructuraCuenta && Number.isFinite(estructuraCuenta.envioPct)) {
    envioPct = estructuraCuenta.envioPct
    fuenteEnvio = { tipo: 'cuenta', lineas: estructuraCuenta.lineas }
  }

  // 2. Ecosistema — categoría publicable.
  const cat = categoryId && ecosistema?.porCategoria?.[categoryId]
  if (cat?.publicable) {
    if (comisionPct == null && Number.isFinite(cat.comisionPct?.mediana)) {
      comisionPct = cat.comisionPct.mediana
      fuenteComision = { tipo: 'eco-categoria', categoryId, ...cat.muestra }
    }
    if (envioPct == null && Number.isFinite(cat.envioPct?.mediana)) {
      envioPct = cat.envioPct.mediana
      fuenteEnvio = { tipo: 'eco-categoria', categoryId, ...cat.muestra }
    }
  }

  // 3. Ecosistema — banda de precio (solo envío: el umbral de gratis lo mueve).
  // La tabla es la del país de la proyección (`ecosistema.siteId`, ADR 2026-08-19
  // §4). Sin país, o un país sin tabla, no hay banda: cae al global, nunca a MLA.
  const idBanda = Number.isFinite(Number(precio)) && Number(precio) > 0 ? bandaPrecioDe(Number(precio), ecosistema?.siteId) : null
  if (envioPct == null && idBanda) {
    const banda = ecosistema?.porBandaPrecio?.[idBanda]
    if (banda?.publicable && Number.isFinite(banda.envioPct?.mediana)) {
      envioPct = banda.envioPct.mediana
      fuenteEnvio = { tipo: 'eco-banda', banda: idBanda, ...banda.muestra }
    }
  }

  // 4. Ecosistema — global (piso).
  if (comisionPct == null && Number.isFinite(ecosistema?.global?.comisionPct?.mediana)) {
    comisionPct = ecosistema.global.comisionPct.mediana
    fuenteComision = { tipo: 'eco-global', ...(ecosistema.muestra || {}) }
  }
  if (envioPct == null && Number.isFinite(ecosistema?.global?.envioPct?.mediana)) {
    envioPct = ecosistema.global.envioPct.mediana
    fuenteEnvio = { tipo: 'eco-global', ...(ecosistema.muestra || {}) }
  }

  if (comisionPct == null && envioPct == null) return null
  // ⚠️ SIN `?? 0`. Devolver 0 por "no sé" es afirmar que el costo es cero, y
  // acá eso infla el margen: el consumidor no puede distinguir "ML no me cobra
  // comisión" de "no tengo el dato". ADR 050 §2 ya lo había decidido por
  // escrito —"nunca fallback a defaults… test de render que asserte la
  // ausencia de 0"— y este era el lugar donde no se respetaba.
  return { comisionPct, envioPct, fuenteComision, fuenteEnvio }
}

// ─────────────────────────────────────────────────────────────────────────────
// v2 — Rentabilidad INFERIDA con la fórmula real + números de decisión
// ─────────────────────────────────────────────────────────────────────────────

function margenConFormula({ subtotal, costoUnit, comisionMonto, envioMonto, adUnit, impuestosEmpresa }) {
  const flags = impuestosEmpresa?.flags || {
    discriminaIVA: true, pagaIIBB: true, pagaImpCredDebito: true, aplicaGananciasEstimado: true,
  }
  return calcularRentabilidadLinea({
    tieneLink: true,
    tieneCosto: true,
    subtotal,
    qty: 1,
    costoUnit,
    comisionMLTotal: comisionMonto,
    envioSellerLinea: envioMonto,
    billing: null,
    adUnit: num(adUnit),
    ivaPct: Number.isFinite(Number(impuestosEmpresa?.ivaGeneral)) ? Number(impuestosEmpresa.ivaGeneral) : 0.21,
    flags,
    iibbRate: num(impuestosEmpresa?.iibb),
    impCredDebitoRate: num(impuestosEmpresa?.impuestoCredDebito),
    factorGanancias: num(impuestosEmpresa?.factorGanancias),
  })
}

/**
 * Rentabilidad INFERIDA de una publicación (sin necesidad de ventas propias)
 * con la MISMA fórmula que las órdenes reales, y los números de decisión:
 *
 *   - margenPct inferido + desglose transparente con fuente por componente
 *   - acosMaxPct       — techo de ACOS antes de vender a pérdida
 *   - inversionMaxAds  — $ máx. de pauta por unidad sin pérdida
 *   - descuentoMaxPct / precioPiso — vs corte (para promos), con envío FIJO
 *     en $ (el envío no baja con el descuento) y comisión proporcional
 *
 * @returns {object|null} null si falta precio o costo (sin datos NO se infiere).
 */
export function inferirRentabilidadML({
  precio, costoUnit, categoryId = null,
  estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null,
  corte = null, adUnit = 0,
} = {}) {
  const p = Number(precio)
  const c = Number(costoUnit)
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(c) || c <= 0) return null

  const costos = resolverCostosML({ estructuraCuenta, ecosistema, categoryId, precio: p })
  // Sin los DOS porcentajes no se infiere. El contrato ya lo decía en su
  // cabecera ("sin datos NO se infiere") y el `?? 0` lo cortocircuitaba: como
  // el ecosistema venía null desde el gate del 4-ago, toda empresa sin historia
  // propia inferían su margen con 0 % de comisión y 0 % de envío.
  // `gatePromoInferido` ya tiene el branch `!inf` → `sin_datos`: que exista es
  // la señal de que éste era el camino previsto.
  if (!costos || !Number.isFinite(costos.comisionPct) || !Number.isFinite(costos.envioPct)) return null
  const comisionPct = costos.comisionPct
  const envioPct = costos.envioPct
  const envioMonto = envioPct * p   // FIJO en $ — no baja con descuentos

  // adUnit = GASTO REAL de ads por unidad (decisión Mariano 15-jul: la renta
  // de la pub carga la plata que salió de verdad; los ads se juzgan aparte
  // con ACOS puro). Fijo en $, como el envío: no baja con descuentos.
  const adNum = Math.max(0, num(adUnit))
  const base = margenConFormula({
    subtotal: p, costoUnit: c,
    comisionMonto: comisionPct * p, envioMonto,
    adUnit: adNum, impuestosEmpresa,
  })
  // Techo de pauta (ACOS máx / inversión máx) SIEMPRE sin ads: es el
  // presupuesto TOTAL tolerable, no el adicional a lo ya gastado.
  const base0 = adNum > 0 ? margenConFormula({
    subtotal: p, costoUnit: c,
    comisionMonto: comisionPct * p, envioMonto,
    adUnit: 0, impuestosEmpresa,
  }) : base

  const rentBruta = num(base0?.breakdown?.rentBrutaPostImp)
  const margenPct = base?.margenPct ?? null

  // Descuento máximo vs corte: bisección con la fórmula completa. Comisión
  // proporcional al precio con descuento; envío fijo en $.
  let descuentoMaxPct = null
  let precioPiso = null
  // OJO: Number(null) === 0 — el corte ausente NO es corte 0.
  const corteNum = (corte == null || corte === '') ? null : Number(corte)
  if (Number.isFinite(corteNum) && margenPct != null) {
    const margenAt = (d) => {
      const sub = p * (1 - d)
      if (sub <= 0) return -Infinity
      const r = margenConFormula({
        subtotal: sub, costoUnit: c,
        comisionMonto: comisionPct * sub, envioMonto,
        adUnit: adNum, impuestosEmpresa,   // el gasto real de ads NO baja con el descuento
      })
      return r?.margenPct ?? -Infinity
    }
    if (margenAt(0) < corteNum) {
      descuentoMaxPct = 0
    } else {
      let lo = 0
      let hi = 0.9
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2
        if (margenAt(mid) >= corteNum) lo = mid
        else hi = mid
      }
      descuentoMaxPct = lo
    }
    precioPiso = p * (1 - descuentoMaxPct)
  }

  return {
    fuente: 'inferida',
    margenPct,
    rentaNeta: base?.rentaNeta ?? null,
    breakdown: base?.breakdown || null,
    comisionPct,
    envioPct,
    fuenteComision: costos?.fuenteComision || null,
    fuenteEnvio: costos?.fuenteEnvio || null,
    // Números de decisión (sheet Mariano+Mateo):
    acosMaxPct: p > 0 ? Math.max(0, rentBruta) / p : null,
    inversionMaxAds: Math.max(0, rentBruta),
    descuentoMaxPct,
    precioPiso,
    corte: Number.isFinite(corteNum) ? corteNum : null,
    adUnit: adNum,
  }
}

/**
 * Gate de promos INFERIDO — reemplazo alineado del gate bruto (ADR 032 v2,
 * decisión Mariano 14-jul: "que sea lo más real posible"). Misma interfaz de
 * estados que gatePromoRentabilidad para swap drop-in en los clasificadores:
 *   sin_datos    — falta costo o corte → se sugiere con ⚠ (no frenamos a ciegas)
 *   con_espacio  — margen inferido > corte → sugerir con descuentoMax/precioPiso
 *   sin_espacio  — margen inferido ≤ corte → NO se sugiere (declarado)
 */
export function gatePromoInferido({ precio, costoUnit, corte, categoryId = null, estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null, adUnit = 0, gastoAdsSinVentas = false } = {}) {
  // Pub que GASTA en ads y no vende nada: la peor señal que existe — jamás
  // cae al benchmark ni recibe promo; lo que corresponde es pausar/ajustar el
  // ad (capa 1). Domina incluso sobre sin_datos (decisión Mariano 15-jul).
  if (gastoAdsSinVentas) {
    const cNum = (corte == null || corte === '') ? null : Number(corte)
    return { estado: 'sin_espacio', motivo: 'ads_gastan_sin_vender', margenActualPct: null, cortePct: Number.isFinite(cNum) ? cNum : null, descuentoMaxPct: 0, precioPiso: null, fuente: 'inferida' }
  }
  // OJO: Number(null) === 0 — el corte ausente NO es corte 0.
  const corteNum = (corte == null || corte === '') ? null : Number(corte)
  if (!(Number(precio) > 0) || !(Number(costoUnit) > 0) || !Number.isFinite(corteNum)) {
    return { estado: 'sin_datos', margenActualPct: null, cortePct: Number.isFinite(corteNum) ? corteNum : null, descuentoMaxPct: null, precioPiso: null, fuente: 'inferida' }
  }
  // adUnit = gasto real de ads por unidad: el margen honesto y el descuento
  // máximo lo cobran (antes la bisección corría con adUnit 0 y mentía).
  const inf = inferirRentabilidadML({ precio, costoUnit, categoryId, estructuraCuenta, ecosistema, impuestosEmpresa, corte: corteNum, adUnit })
  if (!inf || inf.margenPct == null) {
    return { estado: 'sin_datos', margenActualPct: null, cortePct: corteNum, descuentoMaxPct: null, precioPiso: null, fuente: 'inferida' }
  }
  const conEspacio = inf.margenPct > corteNum
  return {
    estado: conEspacio ? 'con_espacio' : 'sin_espacio',
    margenActualPct: inf.margenPct,
    cortePct: corteNum,
    descuentoMaxPct: conEspacio ? inf.descuentoMaxPct : 0,
    precioPiso: conEspacio ? inf.precioPiso : null,
    fuenteComision: inf.fuenteComision,
    fuenteEnvio: inf.fuenteEnvio,
    fuente: 'inferida',
  }
}

/**
 * Resumen de ads de UNA publicación a partir de sus docs de campanasMetricas
 * (metricas.cost / metricas.total_amount, mismos campos que el agregador).
 * Regla 15-jul: los ads se juzgan con ACOS PURO (gasto ÷ ventas atribuidas);
 * la renta de la pub carga el GASTO REAL total. gastoSinVentas=true es la
 * peor señal: gastó y no vendió → candidata a pausar_ad, nunca cae al
 * benchmark "promedio" ni recibe promos.
 * @returns {object|null} null si no hay gasto (sin ads no hay nada que resumir)
 */
export function resumenAdsPublicacion(metricas = []) {
  let cost = 0
  let amount = 0
  for (const m of metricas || []) {
    const c = Number(m?.metricas?.cost ?? m?.cost)
    const a = Number(m?.metricas?.total_amount ?? m?.amount)
    if (Number.isFinite(c) && c > 0) cost += c
    if (Number.isFinite(a) && a > 0) amount += a
  }
  if (cost <= 0) return null
  return {
    cost,
    amount,
    acosPct: amount > 0 ? cost / amount : null,   // null = gastó sin vender (NO es "sin dato bueno")
    gastoSinVentas: amount <= 0,
  }
}

/**
 * RENTA FINAL proyectada: la renta neta DESPUÉS de aplicar las palancas
 * típicas — descuento esperado (subtotal baja, comisión proporcional, envío
 * FIJO en $) y publicidad al ACOS esperado (ad = acos × venta). Es el número
 * que se compara contra la rentabilidad mínima → delta = veredicto.
 */
export function proyectarRentaFinalML({
  precio, costoUnit, categoryId = null,
  estructuraCuenta = null, ecosistema = null, impuestosEmpresa = null,
  acosPct = 0, descuentoPct = 0,
} = {}) {
  const p = Number(precio)
  const c = Number(costoUnit)
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(c) || c <= 0) return null
  const costos = resolverCostosML({ estructuraCuenta, ecosistema, categoryId, precio: p })
  // Mismo criterio que `inferirRentabilidadML`: sin los dos porcentajes, no hay
  // proyección. Un precio sugerido calculado con comisión 0 es peor que ninguno.
  if (!costos || !Number.isFinite(costos.comisionPct) || !Number.isFinite(costos.envioPct)) return null
  const comisionPct = costos.comisionPct
  const envioMonto = costos.envioPct * p   // fijo en $
  const d = Number.isFinite(Number(descuentoPct)) ? Math.min(Math.max(Number(descuentoPct), 0), 0.9) : 0
  const a = Number.isFinite(Number(acosPct)) ? Math.max(Number(acosPct), 0) : 0
  const sub = p * (1 - d)
  const r = calcularRentabilidadLinea({
    tieneLink: true, tieneCosto: true,
    subtotal: sub, qty: 1, costoUnit: c,
    comisionMLTotal: comisionPct * sub,
    envioSellerLinea: envioMonto,
    billing: null,
    adUnit: a * sub,
    ivaPct: Number.isFinite(Number(impuestosEmpresa?.ivaGeneral)) ? Number(impuestosEmpresa.ivaGeneral) : 0.21,
    flags: impuestosEmpresa?.flags || { discriminaIVA: true, pagaIIBB: true, pagaImpCredDebito: true, aplicaGananciasEstimado: true },
    iibbRate: num(impuestosEmpresa?.iibb),
    impCredDebitoRate: num(impuestosEmpresa?.impuestoCredDebito),
    factorGanancias: num(impuestosEmpresa?.factorGanancias),
  })
  return {
    margenFinalPct: r?.margenPct ?? null,
    rentaFinal: r?.rentaNeta ?? null,
    subtotalConDescuento: sub,
    adPorUnidad: a * sub,
    descuentoAplicadoPct: d,
    acosAplicadoPct: a,
  }
}

/**
 * Benchmarks de DECISIÓN (ACOS esperado / descuento típico) con la cascada
 * cuenta → eco-categoría → eco-global (decisión Mariano 14-jul: "si tenemos
 * el de la empresa, tomamos el de la empresa; sino el de la base de
 * conocimiento"). benchmarksEmpresa = empresas/{id}._benchmarksML (lo escribe
 * el agregador nocturno); ecosistema = conocimientoEcosistema/costos-ml.
 */
export function resolverBenchmarksML({ benchmarksEmpresa = null, ecosistema = null, categoryId = null } = {}) {
  let acosPct = null
  let fuenteAcos = null
  let descuentoPct = null
  let fuenteDescuento = null

  const acosEmp = benchmarksEmpresa?.acos
  if (Number.isFinite(acosEmp?.acosPonderado)) {
    acosPct = acosEmp.acosPonderado
    fuenteAcos = { tipo: 'cuenta', items: acosEmp.muestra?.items }
  }
  const descEmp = benchmarksEmpresa?.descuentoPromo
  if (Number.isFinite(descEmp?.descuentoMediana)) {
    descuentoPct = descEmp.descuentoMediana
    fuenteDescuento = { tipo: 'cuenta', pubs: descEmp.muestra?.pubs }
  }

  const cat = categoryId && ecosistema?.porCategoria?.[categoryId]
  if (acosPct == null && cat?.acos?.publicable && Number.isFinite(cat.acos.acosPonderado)) {
    acosPct = cat.acos.acosPonderado
    fuenteAcos = { tipo: 'eco-categoria', categoryId, ...(cat.acos.muestra || {}) }
  }
  if (descuentoPct == null && cat?.descuentoPromo?.publicable && Number.isFinite(cat.descuentoPromo.descuentoMediana)) {
    descuentoPct = cat.descuentoPromo.descuentoMediana
    fuenteDescuento = { tipo: 'eco-categoria', categoryId, ...(cat.descuentoPromo.muestra || {}) }
  }

  if (acosPct == null && Number.isFinite(ecosistema?.global?.acos?.acosPonderado)) {
    acosPct = ecosistema.global.acos.acosPonderado
    fuenteAcos = { tipo: 'eco-global' }
  }
  if (descuentoPct == null && Number.isFinite(ecosistema?.global?.descuentoPromo?.descuentoMediana)) {
    descuentoPct = ecosistema.global.descuentoPromo.descuentoMediana
    fuenteDescuento = { tipo: 'eco-global' }
  }

  if (acosPct == null && descuentoPct == null) return null
  return { acosPct, fuenteAcos, descuentoPct, fuenteDescuento }
}

/**
 * Margen para el VETO del flywheel ("no prender sobre pérdida"), en cascada:
 *   1. margen REAL (rentabilidadInferida.margenPctProm — órdenes de la pub)
 *   2. margen INFERIDO v2 (fórmula completa + cascada de fuentes)
 *   3. margen estimado plano v1 (si solo hay estructura de cuenta)
 *   4. null → sin datos, NO se frena nada
 */
export function margenParaVeto({
  margenReal = null, precio = null, costoUnit = null,
  estructura = null,           // v1 compat (estructura de la cuenta)
  categoryId = null, ecosistema = null, impuestosEmpresa = null,
} = {}) {
  if (Number.isFinite(margenReal)) return { margenPct: margenReal, fuente: 'real' }
  if (ecosistema || impuestosEmpresa) {
    const inf = inferirRentabilidadML({ precio, costoUnit, categoryId, estructuraCuenta: estructura, ecosistema, impuestosEmpresa })
    if (inf && inf.margenPct != null) return { margenPct: inf.margenPct, fuente: 'inferida' }
  }
  const est = margenEstimadoML({ precio, costoUnit, estructura })
  if (est) return { margenPct: est.margenEstimadoPct, fuente: est.fuente }
  return null
}
